import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  Batch,
  BatchMailing,
  BatchMailingType,
  BatchMailingData,
  BatchProperties,
} from '../../models';
import { BehaviorSubject, EMPTY, forkJoin } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { adaptObject, BATCH_MAILING_DATA } from '../../../batches';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import {
  catchError,
  debounceTime,
  finalize,
  mapTo,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { focusFirstInvalidField, triggerFormValidation } from '$core/helpers';
import {
  BatchesParseStorageService,
  BatchesService,
  BatchMailingCreateParams,
  BatchMailingUpdateParams,
} from '../../services';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { validateLocalStoragePerPage } from '$modules/personal/modules/batch/validateLocalStorageBatch';
import { removePhonePrefix } from '$shared/utils';
import { BatchMailingFormComponent } from '../../components';
import { ECOMMERCE_POSTAL_DELIVERY_TYPE } from '../../contants';
import { EditeBatchDepartureModalComponent } from '../../modals/edite-batch-departure-modal/edite-batch-departure-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';

@Component({
  selector: 'app-edit-batch-mailing',
  templateUrl: './edit-batch-mailing.component.html',
  styleUrls: ['./edit-batch-mailing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditBatchMailingComponent implements OnInit, OnDestroy {
  @ViewChild(BatchMailingFormComponent, { static: false })
  public formComponent: BatchMailingFormComponent;

  @ViewChild(BatchMailingFormComponent, { static: false, read: ElementRef })
  public formComponentElement: ElementRef;

  public readonly isFullScreen$ = this.batchesParseStorageService.isFullScreen$;
  public readonly isInitialLoading$ = new BehaviorSubject<boolean>(false);
  public readonly isBatchMailingProcessing$ = new BehaviorSubject<boolean>(
    false
  );
  public readonly batchProperties$ =
    new BehaviorSubject<BatchProperties | null>(null);
  public readonly batchMailingData$ = new BehaviorSubject<
    BatchMailingData[BatchMailingType] | null
  >(null);
  public readonly batch$ = new BehaviorSubject<Batch | null>(null);

  public readonly batchId = this.activatedRoute.snapshot.params['id'];
  public readonly batchMailingId =
    this.activatedRoute.snapshot.queryParams['departureId'];
  private perPage = 15;

  private readonly BATCH_MAILING_DATA = BATCH_MAILING_DATA;

  constructor(
    private readonly modalService: BsModalService,
    private readonly cd: ChangeDetectorRef,
    private readonly batchesService: BatchesService,
    private readonly batchesParseStorageService: BatchesParseStorageService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly renderer: Renderer2
  ) {}

  @HostListener('document:keydown.escape')
  public onEscapeKeyPress(): void {
    if (this.isFullScreen$.getValue()) {
      this.toggleFullScreen();
    }
  }

  public ngOnInit(): void {
    this.loadData();

    if (localStorage.getItem('batch-departures-per-page')) {
      this.perPage = validateLocalStoragePerPage('batch-departures-per-page');
    }
  }

  public ngOnDestroy(): void {}

  public back(): void {
    if (this.batchMailingId && this.batchProperties.isPartialReceipt) {
      this.router.navigate(
        [`personal/batch/batches/${this.batchId}/view-departure-attachments`],
        {
          queryParams: {
            departureId: this.batchMailingId,
            perPage: this.perPage,
          },
        }
      );
    } else {
      this.router.navigate(
        [`personal/batch/batches/${this.batchId}/view-batch`],
        {
          queryParams: {
            perPage: this.perPage,
          },
        }
      );
    }
  }

  public submit(): void {
    if (this.form.invalid) {
      triggerFormValidation(this.form);
      focusFirstInvalidField(this.formComponentElement);

      if (
        this.batchProperties.isOrderedLetterOrPostcard &&
        this.form.get('hand_over_personally').value === true &&
        this.form.get('hand_over_personally').invalid
      ) {
        this.form.get('hand_over_personally').patchValue(null);
      }

      return;
    }

    this.editBatchMailing(this.form);
  }

  public navigateToNextPage(resp: BatchMailing): void {
    if (this.batchProperties.isPartialReceipt) {
      this.router.navigate(
        [
          `personal/batch/batches/${this.batch.id}/created/view-departure-attachments`,
        ],
        {
          queryParams: {
            departureId: resp,
            perPage: this.perPage,
          },
        }
      );
    } else {
      this.router.navigate(
        [`personal/batch/batches/${this.batch.id}/view-batch`],
        {
          queryParams: {
            departureId: resp.id,
            perPage: this.perPage,
          },
        }
      );
    }
  }

  public toggleFullScreen(): void {
    this.batchesParseStorageService.toggleFullScreen(this.renderer);
  }

  public get batchProperties(): BatchProperties | null {
    return this.batchProperties$.value;
  }

  private loadData(): void {
    this.isInitialLoading$.next(true);

    forkJoin({
      batch: this.batchesService.show({ batchId: this.batchId }),
    })
      .pipe(
        switchMap(({ batch }) =>
          this.batchesService
            .showMailing({
              batchId: this.batchId,
              mailingId: this.batchMailingId,
            })
            .pipe(
              tap((mailing) => {
                const properties = this.setupBatchProperties(batch);

                this.batch$.next(batch);
                this.batchProperties$.next(properties);
                this.batchMailingData$.next(
                  this.BATCH_MAILING_DATA[batch.postal_delivery_type]
                );

                setTimeout(() => {
                  this.fillFormGroup(this.form, mailing);

                  if (this.form.get('notification').value === '5') {
                    this.formComponent.setObtainingMethod();
                  }

                  this.observeForm();
                }, 0);
              })
            )
        ),
        finalize(() => this.isInitialLoading$.next(false)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private fillFormGroup(form: FormGroup, data: BatchMailing): void {
    const addonKeys: string[] = [];

    Object.keys(data).forEach((key) => {
      const value = data[key];

      if (key === 'recipient_contact_widget_data') {
        form.get('recipient').setValue(value, { emitEvent: false });
      } else if (
        form.controls[key] &&
        key !== 'recipient_contact_widget_data'
      ) {
        form.controls[key].patchValue(value, { emitEvent: false });
      }

      if (key === 'addons' && typeof value === 'object') {
        Object.keys(value).forEach((addonKey) => {
          let addonValue = value[addonKey];

          if (addonKey === 'phone' && addonValue) {
            addonValue = removePhonePrefix(addonValue);
          }

          if (form.get(addonKey) && addonKey !== 'notification') {
            form.get(addonKey).patchValue(addonValue, { emitEvent: false });
            if (addonValue === true) {
              addonKeys.push(addonKey);
            }
          }
        });
      }
    });

    this.formComponent.handleTimeOfDelivery();
    this.formComponent.addonsControl.patchValue(addonKeys, {
      emitEvent: false,
    });
  }

  private observeForm(): void {
    this.formComponent.form$
      .pipe(
        switchMap((form) =>
          form.valueChanges.pipe(
            startWith(form.value),
            debounceTime(500),
            untilDestroyed(this)
          )
        )
      )
      .subscribe(() => {
        if (this.form.valid) {
          this.calculate();
        }
      });
  }

  private calculate(): void {
    const body = adaptObject(this.form.getRawValue());

    this.isBatchMailingProcessing$.next(true);

    this.batchesService
      .calculate(this.batch.id, body)
      .pipe(
        catchError((errors) => {
          if (errors.error.errors) {
            this.parseErrors(errors.error.errors);
          }

          this.cd.detectChanges();

          return EMPTY;
        }),
        finalize(() => this.isBatchMailingProcessing$.next(false)),
        untilDestroyed(this)
      )
      .subscribe((response) => {
        this.form.get('cost').patchValue(response.cost, { emitEvent: false });
        this.formComponent.markForCheck();
      });
  }

  private parseErrors(errors: Params): void {
    Object.keys(errors).forEach((key) => {
      const error = errors[key];
      if (key.includes('addons.')) {
        key = key.replace('addons.', '');
      }

      const [items, controlIndex, controlName] = key.split('.');
      if (items && items.startsWith('items')) {
        const formGroup = this.form;
        const control = formGroup.get(controlName);
        if (
          control &&
          control.statusChanges &&
          formGroup &&
          formGroup.statusChanges
        ) {
          control.setErrors(error, { emitEvent: false });
          control.markAsTouched();
          formGroup.markAsDirty();
        } else {
          console.error(`Control with name ${controlName} not found`);
        }
      } else {
        const formGroup = this.form;

        switch (key) {
          case 'recipient_contact_widget_data': {
            const control = formGroup.get('recipient');

            const recipientErrors = error.map((item) => {
              switch (item) {
                case 'validation.recipient_contact_widget_data_required':
                  return 'Обязательное поле';
                default:
                  return item;
              }
            });

            if (!control) return;

            control.setErrors(recipientErrors, { emitEvent: false });
            control.markAsTouched({ onlySelf: true });

            formGroup.markAsDirty();

            break;
          }

          default: {
            const control = formGroup.get(key);

            if (!control) return;

            control.setErrors(error, { emitEvent: false });
            control.markAsTouched({ onlySelf: true });
            formGroup.markAsDirty();

            break;
          }
        }
      }
    });

    this.cd.detectChanges();
    this.formComponent.markForCheck();
  }

  private editBatchMailing(form: FormGroup): void {
    const params: BatchMailingUpdateParams = {
      batchId: this.batch.id,
      mailingId: this.batchMailingId,
      ...adaptObject(form.getRawValue()),
    };

    this.isBatchMailingProcessing$.next(true);
    this.batchesService
      .updateMailing(params)
      .pipe(
        switchMap((batchDeparture) => {
          const modalRef = this.modalService.show(
            EditeBatchDepartureModalComponent,
            {
              ignoreBackdropClick: false,
              class: 'modal-dialog-centered',
              initialState: {
                batchDeparture,
              },
            }
          );

          return modalRef.onHide.pipe(take(1), mapTo(true));
        }),
        catchError((errors) => {
          if (errors.error.errors) {
            this.parseErrors(errors.error.errors);
          }

          return EMPTY;
        }),
        finalize(() => this.isBatchMailingProcessing$.next(true)),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => {
          return this.router.navigate(
            [
              `personal/batch/batches/${this.batch.id}/${
                this.batchProperties.isPartialReceipt
                  ? 'view-departure-attachments'
                  : 'view-batch'
              }`,
            ],
            {
              queryParams: {
                perPage: this.perPage,
                departureId: this.batchMailingId,
                ...(this.batchProperties.isPartialReceipt && {
                  activeTab: '1',
                }),
              },
            }
          );
        },
      });
  }

  private setupBatchProperties(batch: Batch): BatchProperties {
    return {
      isEcommerce: ECOMMERCE_POSTAL_DELIVERY_TYPE.has(
        batch.postal_delivery_type
      ),
      isELS: batch.payment_type === 'electronic_personal_account',
      isCommitmentLetter: batch.payment_type === 'commitment_letter',
      isEMS: batch.postal_delivery_type === 'ems',
      isPartialReceipt: batch.is_partial_receipt,
      isOrderedLetterOrPostcard:
        batch.postal_delivery_type === 'ordered_letter' ||
        batch.postal_delivery_type === 'ordered_postcard',
    };
  }

  private get batch(): Batch | null {
    return this.batch$.value;
  }

  private get form(): FormGroup | null {
    if (!this.formComponent) {
      return null;
    }

    return this.formComponent.form;
  }
}
